import { useCallback, useMemo, useState } from 'react';
import { FieldValues, Path, PathValue, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Text } from '@chakra-ui/react';
import { FormInput, FormInputProps } from '@netiva/classifieds-ui';
import { useMessagingService } from '@netiva/classifieds-common';

import { isRequiredValueMissing } from './requiredValidation';

const EMAIL_VALIDATION_TIMEOUT_MS = 5_000;

type ValidationResponse = {
  isValidFormat: boolean;
  isValidHost?: boolean;
  suggestion?: string;
};

/**
 * Email input component for use in a react-hook-form context that validates the email address using the Netiva.MessagingService address validation endpoint.
 * Requires the component to be wrapped in a MessagingServiceProvider.
 */
export function FormEmailInput<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  isRequired,
  pattern,
  size,
  ...rest
}: FormInputProps<TFieldValues>) {
  const { baseUrl } = useMessagingService();

  if (!baseUrl) {
    throw new Error(
      'For the FormEmailInput control to function you need to specify the <env-prefix>_MESSAGING_SERVICE_API_URL environment variable.'
    );
  }

  const { clearErrors, setValue } = useFormContext();
  const { t } = useTranslation('forms');
  const [suggestion, setSuggestion] = useState<string>();

  const handleSuggestionClick = useCallback(() => {
    // @ts-ignore target field type must be string; correct typing would complicate the function definition unnecessarily
    setValue(name, suggestion);
    clearErrors(name);
    setSuggestion(undefined);
  }, [clearErrors, name, setValue, suggestion]);

  const suggestionText = useMemo(() => {
    if (!suggestion) {
      return null;
    }
    return (
      <Text cursor="pointer" onClick={handleSuggestionClick}>
        {t('form.validationError.emailSuggestion', { suggestion })}
      </Text>
    );
  }, [handleSuggestionClick, suggestion, t]);

  const defaultPattern = useMemo(() => {
    return {
      value: /^\S+@\S+$/i,
      message: t('form.validationError.invalid', { field: label }),
    };
  }, [label, t]);

  const validate = useCallback(
    async (value: PathValue<TFieldValues, Path<TFieldValues>>) => {
      setSuggestion(undefined);
      if (isRequiredValueMissing(value)) {
        return isRequired ? t('form.validationError.required', { field: label }) : true;
      }

      let response: ValidationResponse | null;
      try {
        const url = `${baseUrl.replaceAll(/\/$/g, '')}/v1/Email/ValidateAddress?address=${encodeURIComponent(value)}`;
        const httpResponse = await fetch(url, { signal: AbortSignal.timeout(EMAIL_VALIDATION_TIMEOUT_MS) });
        if (!httpResponse.ok) {
          return true;
        }
        response = (await httpResponse.json()) as ValidationResponse | null;
      } catch {
        return true;
      }
      if (typeof response?.isValidFormat !== 'boolean') {
        return true;
      }

      setSuggestion(typeof response.suggestion === 'string' ? response.suggestion : undefined);
      if (!response.isValidFormat || response.isValidHost === false) {
        return t('form.validationError.invalid', { field: label });
      }
      return true;
    },
    [baseUrl, isRequired, label, t]
  );

  return (
    <FormInput
      {...rest}
      name={name}
      label={label}
      isRequired={isRequired}
      pattern={pattern || defaultPattern}
      validate={validate}
      description={suggestionText || description}
      size={size}
    />
  );
}
